import { Component, computed, effect, inject, input, InputSignal, output, OutputEmitterRef, signal, Signal } from '@angular/core';
import { Regimen, User } from '@cureskin/api-client/src/server-api';
import { CommonModule } from '@angular/common';
import { ApiClientConstant } from '@cureskin/api-client';
import { CsIconModule } from '@components/icon';
import { CurePlanIncludes } from '@cureskin/ui-angular';
import { ExperimentService } from '@services/experiment/experiment.service';
import { TrackScrollDepthDirective } from '@directives/track-scroll-depth/track-scroll-depth.directive';
import { planName, selectedPlanAndVariantIdMap } from '@services/regimen-service';
import { getPlanInfoConfig, PlanInfoItem, planInfoItemId, planInfoImageUrls } from '../choose-plan-info/choose-plan-info.constants';

const VARIANT_4999 = 'variant_4999';

/** Item shape expected by the <cure-plan-includes> composite (regimen_value_prop_v3 path). */
interface PlanIncludeCard {
  label: string;
  imageUrl?: string;
  shortLabel?: boolean;
  subLabel?: string;
}

const BG_COLOR_CLASS_TO_TOKEN: Record<string, string> = {
  'tw-bg-teal-100': 'teal-100',
  'tw-bg-orange-100': 'orange-100',
  'tw-bg-blue-100': 'blue-100',
};

@Component({
  selector: 'regimen-value-proposition-v2',
  imports: [CommonModule, CsIconModule, CurePlanIncludes, TrackScrollDepthDirective],
  templateUrl: './regimen-value-proposition-v2.component.html',
})
export class RegimenValuePropositionV2Component {
  private readonly experimentService = inject(ExperimentService);

  regimenUpdateTrigger: InputSignal<Regimen> = input();
  regimenChangeSignal: InputSignal<Regimen> = input();
  regimenData = signal<Regimen | null>(null);

  user: InputSignal<User> = input();
  backgroundColor: InputSignal<string> = input();
  allocatedDoctorImage: InputSignal<string> = input();
  patientName: Signal<string> = computed(() => this.user()?.get('PatientName')?.split(' ')[0] || '');
  regimenProductsLength: Signal<number> = computed(() => this.regimenData()?.products?.length || 0);
  readMoreClick: OutputEmitterRef<void> = output();
  moneyBackClick: OutputEmitterRef<void> = output();
  readonly ApiClientConstant = ApiClientConstant;
  planInfoItemId = planInfoItemId;

  // --- regimen_value_prop_v3 (composite) path ---

  private readonly yourPlanIncludes = $localize`:@@yourPlanIncludes:Your Plan includes`;

  /** true → render the <cure-plan-includes> composite; false → legacy hand-rolled template. */
  get showV3Composite(): boolean {
    return this.experimentService.isRegimenValuePropV3Enabled();
  }

  // Embedded money-back banner (`showMoneyback`, ui-core ≥0.9.1). The band stays `interactive`
  // for the readMore tap; `moneybackInteractive` makes the banner its own press target. Press
  // targets are EXCLUSIVE in the lib (≥0.9.1): the banner stops BOTH its custom `curePress` and
  // the native click, so a banner tap fires only `cureMoneybackPress` (→ moneyBackClick) and a
  // band tap fires only `curePress` (→ readMore) — no double-fire. (0.9.0 only stopped the custom
  // event; the native click still bubbled into the band <button> and double-fired `curePress`.)
  showMoneyBack: InputSignal<boolean> = input(false);
  moneyBackPercentText: InputSignal<string> = input('100%');
  moneyBackTitleText: InputSignal<string> = input('MONEY BACK');
  moneyBackSubtitleText: InputSignal<string> = input('GUARANTEE');
  moneyBackActionText: InputSignal<string> = input('See Details');

  headingText: Signal<string> = computed(() => `${this.patientName()}, ${this.yourPlanIncludes}`);

  planIncludesBgColor: Signal<string> = computed(() => {
    const bg = this.backgroundColor();
    return BG_COLOR_CLASS_TO_TOKEN[bg] ?? 'teal-100';
  });

  planItems: Signal<PlanIncludeCard[]> = computed(() => {
    const regimenData = this.regimenData();
    if (!regimenData) return [];

    const selectedVariantId = (regimenData as any)?.selectedVariantId || (regimenData as any)?.variantId;
    const regimenClass = (regimenData as any)?.class;

    return this.buildCards(regimenClass, selectedVariantId);
  });

  constructor() {
    effect(() => {
      const updatedRegimen = this.regimenUpdateTrigger();
      if (updatedRegimen) {
        this.regimenData.set(updatedRegimen);
      }
    });
  }

  readMoreAboutServices(): void {
    this.readMoreClick.emit();
  }

  onMoneyBackClick(): void {
    this.moneyBackClick.emit();
  }

  private buildCards(regimenClass: string, selectedVariantId: string): PlanIncludeCard[] {
    const isHair = regimenClass === ApiClientConstant.Regimen.Class.HAIR;
    const productLabel = isHair
      ? $localize`:@@HAIR_PRODUCTS_WITH_COUNT:${this.regimenProductsLength()} Hair Products`
      : $localize`:@@SKIN_PRODUCTS_WITH_COUNT:${this.regimenProductsLength()} Skin Products`;

    const doctorCheckupsLabel = $localize`:@@DOCTOR_CHECKUPS_3:3 Doctor Checkups`;
    const dietPlanLabel = $localize`:@@DIET_PLAN:Diet Plan`;
    const nutritionistSubLabel = $localize`:@@PLUS_2_CALLS_NUTRITIONIST:+ 2 calls with nutritionist`;

    return [
      {
        label: productLabel,
        imageUrl: this.regimenData()?.regimenCoverImage,
      },
      {
        label: doctorCheckupsLabel,
        imageUrl: planInfoImageUrls.CHECKUP_REPORT_SQUARE,
      },
      {
        label: dietPlanLabel,
        imageUrl: planInfoImageUrls.DIET_PLAN_SQUARE,
        shortLabel: true,
        subLabel: selectedVariantId === VARIANT_4999 ? nutritionistSubLabel : undefined,
      },
    ];
  }

  // --- Legacy path (pre-v3 hand-rolled template) ---

  // Cache the config to avoid creating new objects on every access.
  private _planInfoConfigCache: ReturnType<typeof getPlanInfoConfig> | null = null;
  private _lastCacheKey: string | null = null;

  // Computed signal to get current plan info config based on variantId.
  currentPlanInfo = computed(() => {
    const regimenData = this.regimenData();
    const variantId = (regimenData as any)?.variantId;
    const selectedVariantId = (regimenData as any)?.selectedVariantId || variantId;
    const regimenClass = (regimenData as any)?.class;
    const videoCall = this.experimentService.videoCallExperiment;
    const currentPlan = Object.values(planName).find(
      (plan) => selectedPlanAndVariantIdMap[plan] === selectedVariantId,
    );
    const cacheKey = `${regimenClass}-${selectedVariantId}-${videoCall}`;
    if (!this._planInfoConfigCache || this._lastCacheKey !== cacheKey) {
      this._planInfoConfigCache = getPlanInfoConfig([], videoCall, regimenClass);
      this._lastCacheKey = cacheKey;
    }
    return this._planInfoConfigCache[currentPlan || planName.advance];
  });

  getItemImage(item: PlanInfoItem): string {
    if (item.id === planInfoItemId.PRODUCTS && this.regimenData()?.regimenCoverImage) {
      return this.regimenData()?.regimenCoverImage;
    }
    if ((item.id === planInfoItemId.DEDICATED_DOCTOR || item.id === planInfoItemId.ONE_ON_ONE_DOCTOR) && this.allocatedDoctorImage()) {
      return this.allocatedDoctorImage();
    }
    switch (item.id) {
      case planInfoItemId.MONTHLY_CHECKUPS:
        return planInfoImageUrls.CHECKUP_REPORT_SQUARE;
      case planInfoItemId.NUTRITIONIST_SESSION:
      case planInfoItemId.NUTRITIONIST_SESSIONS:
        return planInfoImageUrls.CALL_ICONS_SQUARE;
      case planInfoItemId.DIET_PLAN:
        return planInfoImageUrls.DIET_PLAN_SQUARE;
      default:
        return item.imageUrl;
    }
  }
}
